import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { filter, get, groupBy, isEmpty, map, reduce } from 'lodash';
import { ComponentType, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { AttestationStatus } from '../../../enums/application-status.enum';
import { useQuestionCategories, useRouter } from '../../../hooks';
import { getAttestation } from '../../../thunks/attestation-thunks';
import { Loader } from '../../loader';
import { Page } from '../../page';
import useStyles from '../test-exam/styles';
import { TestQuestions } from '../test-questions';
import { TestQuestionsNav } from '../test-questions-nav';
import { TestWait } from '../test-wait';

export const TestResult: ComponentType = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const router = useRouter();
  const { t } = useTranslation();
  const { categoriesMap } = useQuestionCategories();
  const { one } = useSelector((state: RootStateOrAny) => state.attestation);
  const [scrollTo, setScrollTo] = useState<number>();

  const mode = 'result';
  const id = get(router, ['query', 'id']);
  const { isLoading, data } = one;
  const { attestationStatus, questions, isPassed, score } = data || {};
  const total = (questions || []).length;
  const userAnswers = map(questions, ({ id, userAnswer }) => ({ question: id, userAnswer }));
  const selected = map(questions, ({ id, isCorrect }) => ({ question: id, isCorrect }));
  const done = filter(questions, ({ isCorrect }) => isCorrect).length;

  const text = isPassed
    ? t(
        'test.result.text.success',
        `Вы прошли аттестацию. Вы ответили правильно на ${done} вопросов из ${total}.`,
        { done, total }
      )
    : t(
        'test.result.text.fail',
        `К сожалению, Вы не прошли аттестацию. Вы ответили правильно на ${done} вопросов из ${total}.`,
        { done, total }
      );

  const renderScoresByCategory = () => {
    if (isEmpty(questions) || isEmpty(categoriesMap)) return null;

    const groupByCategory = groupBy(questions, ({ category }) =>
      get(categoriesMap, category, 'Без категории')
    );
    return map(Object.keys(groupByCategory), (category, idx) => {
      return (
        <Typography variant="subtitle2" key={idx}>
          {category}:{' '}
          {reduce(groupByCategory[category], (sum, { score }) => sum + score, 0).toFixed(1)}
        </Typography>
      );
    });
  };

  useEffect(() => {
    if (id) dispatch(getAttestation(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const view = () => {
    if (isLoading) return <Loader />;

    if (attestationStatus === AttestationStatus.COMPLETED) {
      return (
        <div className={classes.root}>
          <Box mb={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color={isPassed ? 'success.main' : 'error'} mb={1}>
                  {text}
                </Typography>
                <Typography variant="h3" color={isPassed ? 'success.main' : 'error'} mb={1}>
                  {t('test.result.score', 'Ваша оценка: ' + score, {
                    score: Number(score).toFixed(1),
                  })}
                </Typography>
                {renderScoresByCategory()}
              </CardContent>
            </Card>
          </Box>
          <Stack direction="row" spacing={4} className={classes.grid}>
            <TestQuestions
              {...{ questions, userAnswers }}
              highlightError
              disabled={mode === 'result'}
              qIndex={scrollTo}
            />
            <Stack width={288}>
              <TestQuestionsNav
                {...{ selected, questions, total, done, mode, isPassed }}
                scrollTo={setScrollTo}
              />
            </Stack>
          </Stack>
        </div>
      );
    }

    return <TestWait data={data} />;
  };

  return (
    <Page title={t('testing', 'Тестирование')} layout="empty">
      {view()}
    </Page>
  );
};
